import { z } from "zod"

export type DayOfWeek =
  | "monday"
  | "tuesday"
  | "wednesday"
  | "thursday"
  | "friday"
  | "saturday"
  | "sunday"

export type LessonAvailability = "daytime" | "evening" | "weekend"
export const LessonAvailabilityEnum = z.enum(["daytime", "evening", "weekend"])
export const daysOfWeek: DayOfWeek[] = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
]

export const DaysOfWeekEnum = z.enum(
  [
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
    "sunday",
  ],
  {
    message: "Selecteer een geldige dag van de week",
  },
)

export const timeSlotsEnum = z.enum(
  ["morning", "midday", "afternoon", "evening"],
  {
    message: "Selecteer een geldig tijdslot",
  },
)

const daysOfWeekSet = new Set<string>(daysOfWeek)
export const weekendDays = ["saturday", "sunday"]

export type Day = (typeof daysOfWeek)[number]
export type Slot = "morning" | "midday" | "afternoon" | "evening"
export type WeekendSlot = "morning" | "afternoon" | "evening"

export const timeSlotsByDay: Record<Day, Slot[]> = {
  monday: ["morning", "midday", "afternoon", "evening"],
  tuesday: ["morning", "midday", "afternoon", "evening"],
  wednesday: ["morning", "midday", "afternoon", "evening"],
  thursday: ["morning", "midday", "afternoon", "evening"],
  friday: ["morning", "midday", "afternoon", "evening"],
  saturday: ["morning", "afternoon", "evening"],
  sunday: ["morning", "afternoon", "evening"],
}

export const dayLabels: Record<Day, string> = {
  monday: "Maandag",
  tuesday: "Dinsdag",
  wednesday: "Woensdag",
  thursday: "Donderdag",
  friday: "Vrijdag",
  saturday: "Zaterdag",
  sunday: "Zondag",
}

export const slotLabels: Record<Slot, string> = {
  morning: "Ochtend",
  midday: "Middag",
  afternoon: "Namiddag",
  evening: "Avond",
}

export const slotTimes: Record<
  Slot,
  {
    startTime: string
    endTime: string
  }
> = {
  morning: {
    startTime: "09:00",
    endTime: "12:00",
  },
  midday: {
    startTime: "12:00",
    endTime: "15:00",
  },
  afternoon: {
    startTime: "15:00",
    endTime: "18:00",
  },
  evening: {
    startTime: "18:30",
    endTime: "21:30",
  },
}

export const weekendSlotTimes: Record<
  WeekendSlot,
  {
    startTime: string
    endTime: string
  }
> = {
  morning: {
    startTime: "10:00",
    endTime: "13:00",
  },
  afternoon: {
    startTime: "13:30",
    endTime: "16:30",
  },
  evening: {
    startTime: "18:30",
    endTime: "21:30",
  },
}

export const availabilitySlotTimes: Record<
  Slot,
  {
    startTime: string
    endTime: string
  }
> = {
  morning: {
    startTime: "08:30",
    endTime: "12:00",
  },
  midday: {
    startTime: "12:00",
    endTime: "16:00",
  },
  afternoon: {
    startTime: "15:00",
    endTime: "19:00",
  },
  evening: {
    startTime: "18:30",
    endTime: "21:30",
  },
}

export const getDayFromDate = (date: string | Date) => {
  const dateObj = typeof date === "string" ? new Date(date) : date
  const dayIndex = dateObj.getDay()

  // getDay() returns 0 for Sunday, 1 for Monday, etc.
  // Convert to our array index (Monday = 0)
  const adjustedIndex = dayIndex === 0 ? 6 : dayIndex - 1

  return daysOfWeek[adjustedIndex]
}

export function getSlotTimes(
  dayOrDate: Day | string | Date,
  slot: Slot,
): { startTime: string; endTime: string } {
  // Check if it's a Day type or a date - O(1) lookup met Set
  const day =
    typeof dayOrDate === "string" && daysOfWeekSet.has(dayOrDate)
      ? (dayOrDate as Day)
      : getDayFromDate(dayOrDate as string | Date)

  const isWeekend = day === "saturday" || day === "sunday"

  if (isWeekend && slot !== "midday") {
    return weekendSlotTimes[slot as WeekendSlot]
  }

  return slotTimes[slot]
}

export const lessonAvailabilityOptions: {
  label: string
  value: LessonAvailability
}[] = [
  { label: "Overdag", value: "daytime" },
  { label: "Avond", value: "evening" },
  { label: "Weekend", value: "weekend" },
]

export const lessonAvailabilityLabels: Record<
  string,
  { label: string; time: string }
> = {
  daytime: {
    label: "Overdag (ma t/m vr)",
    time: slotTimes.morning.startTime + " - " + slotTimes.afternoon.endTime,
  },
  evening: {
    label: "'s Avonds (ma t/m vr)",
    time: slotTimes.evening.startTime + " - " + slotTimes.evening.endTime,
  },
  weekend: {
    label: "Weekend (za & zo)",
    time: slotTimes.morning.startTime + " - " + slotTimes.evening.endTime,
  },
}

export const slotOptions = [
  { label: "Ochtend", value: "morning" },
  { label: "Middag", value: "midday" },
  { label: "Namiddag", value: "afternoon" },
  { label: "Avond", value: "evening" },
] as const

/** Normalizes legacy scalar responses and persisted drafts to an ordered set. */
export function normalizeLessonAvailability(
  value: LessonAvailability | LessonAvailability[] | null | undefined,
): LessonAvailability[] {
  const values = Array.isArray(value) ? value : value ? [value] : []

  return lessonAvailabilityOptions
    .map(({ value }) => value)
    .filter((period) => values.includes(period))
}

export function formatLessonAvailability(
  value: LessonAvailability | LessonAvailability[],
) {
  const periods = normalizeLessonAvailability(value)

  return (
    lessonAvailabilityOptions
      .filter(({ value }) => periods.includes(value))
      .map(({ label }) => label)
      .join(", ") || "-"
  )
}

export function coversLessonAvailability(
  required: LessonAvailability | LessonAvailability[],
  available: LessonAvailability[] | null | undefined,
) {
  const periods = normalizeLessonAvailability(required)

  return (
    periods.length > 0 && periods.every((period) => available?.includes(period))
  )
}

export function getAvailableScheduleDays(
  availability: LessonAvailability | LessonAvailability[],
): DayOfWeek[] {
  const periods = normalizeLessonAvailability(availability)

  return daysOfWeek.filter((day) =>
    weekendDays.includes(day)
      ? periods.includes("weekend")
      : periods.includes("daytime") || periods.includes("evening"),
  )
}

export function getAvailableScheduleTimeSlots(
  availability: LessonAvailability | LessonAvailability[],
  day: DayOfWeek,
): Slot[] {
  const periods = normalizeLessonAvailability(availability)
  if (weekendDays.includes(day)) {
    return periods.includes("weekend")
      ? ["morning", "afternoon", "evening"]
      : []
  }

  return [
    ...(periods.includes("daytime")
      ? (["morning", "midday", "afternoon"] as Slot[])
      : []),
    ...(periods.includes("evening") ? (["evening"] as Slot[]) : []),
  ]
}
